import React, { useEffect, useState } from "react";
import { employees, getEmployeeName } from "../../Config/Employees";
import {
  Button,
  Badge,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Progress,
  Select,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Text,
  Textarea,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { DateTime } from "luxon";
import EmployeeDataService from "../../services/Employee";
import { getUserAccess } from "../../utils/authUtils";
import { HrmsTableFrame } from "../../components/Hrms/HrmsTable";
import { PersonCell } from "../../components/Hrms/hrmsUi";
import { HrmsPageShell } from "../../components/Hrms/HrmsPageShell";
import { hrmsDateBounds, validateTargetDeadline } from "./hrmsDateRules";

export const EmployeeTargets = () => {
  const token = localStorage.getItem("logged_in_user_token");
  const userEmail = localStorage.getItem("logged_in_email") || "";
  const user = localStorage.getItem("logged_in_email") || "";
  const { isAdmin } = getUserAccess();
  const toast = useToast();

  const [isLoading, setIsLoading] = useState(false);
  const [targets, setTargets] = useState<any[]>([]);
  const [filterEmail, setFilterEmail] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  // Create target modal (admin)
  const {
    isOpen: isCreateOpen,
    onOpen: onCreateOpen,
    onClose: onCreateClose,
  } = useDisclosure();
  const [newTarget, setNewTarget] = useState({
    email: "",
    targetTitle: "",
    targetDescription: "",
    deadline: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Update progress modal
  const {
    isOpen: isUpdateOpen,
    onOpen: onUpdateOpen,
    onClose: onUpdateClose,
  } = useDisclosure();
  const [selectedTarget, setSelectedTarget] = useState<any>(null);
  const [updateProgress, setUpdateProgress] = useState(0);
  const [updateStatus, setUpdateStatus] = useState("");


  const getStatusBadge = (status: string) => {
    const colorMap: any = {
      pending: "gray",
      in_progress: "blue",
      completed: "green",
      overdue: "red",
    };
    return (
      <Badge colorScheme={colorMap[status] || "gray"}>
        {status?.replace(/_/g, " ")}
      </Badge>
    );
  };

  const fetchTargets = async () => {
    setIsLoading(true);
    try {
      const res = await EmployeeDataService.getTargets(
        token,
        user,
        isAdmin ? filterEmail : ""
      );
      if (res.data?.data) {
        setTargets(res.data.data);
      }
    } catch (e: any) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  };

  const bounds = hrmsDateBounds();

  const handleCreateTarget = async () => {
    if (!newTarget.email || !newTarget.targetTitle || !newTarget.deadline) {
      toast({
        position: "top",
        title: "Please fill all required fields",
        status: "warning",
        duration: 2000,
        isClosable: true,
      });
      return;
    }
    const dateError = validateTargetDeadline(newTarget.deadline);
    if (dateError) {
      toast({ position: "top", title: dateError, status: "error", duration: 3000, isClosable: true });
      return;
    }

    setIsSubmitting(true);
    try {
      await EmployeeDataService.createTarget(token, { ...newTarget, name: getEmployeeName(newTarget.email) }, user);
      toast({
        position: "top",
        title: "Target created successfully",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      onCreateClose();
      setNewTarget({ email: "", targetTitle: "", targetDescription: "", deadline: "" });
      fetchTargets();
    } catch (e: any) {
      toast({
        position: "top",
        title: "Failed to create target",
        description: e.response?.data?.message || "Something went wrong",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateTarget = async () => {
    if (!selectedTarget) return;
    try {
      await EmployeeDataService.updateTarget(token, selectedTarget._id, {
        progress: updateProgress,
        status: updateStatus,
      });
      toast({
        position: "top",
        title: "Target updated successfully",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      onUpdateClose();
      fetchTargets();
    } catch (e: any) {
      toast({
        position: "top",
        title: "Update failed",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    fetchTargets();
  }, []);

  const filteredTargets = filterStatus
    ? targets.filter((t: any) => t.status === filterStatus)
    : targets;

  return (
    <HrmsPageShell
      title="Targets"
      description={isAdmin ? "Set and track targets for employees" : "Your targets and progress"}
      sectionKey="me"
      isLoading={isLoading}
    >
      <>
        {/* Filters */}
        <Flex justify="space-between" align="center" flexWrap="wrap" gap={4} mb={6}>
          {isAdmin && (
            <Button
              bg="#327171"
              color="white"
              onClick={onCreateOpen}
              _hover={{ bg: "#285e5e" }}
            >
              Set New Target
            </Button>
          )}

          <Flex alignItems="flex-end" justifyContent="end" gap={8} flexWrap="wrap" flex="1">
            {isAdmin && (
              <Flex display="flex" alignItems="flex-end" w="300px">
                <Select
                  placeholder="Select Employee"
                  value={filterEmail}
                  onChange={(e) => setFilterEmail(e.target.value)}
                  color="#327171"
                  borderRightRadius={0}
                  focusBorderColor="teal"
                  borderWidth="0.2px"
                  _focus={{ borderWidth: "0.2px", borderColor: "teal.200" }}
                >
                  {employees.map((emp) => (
                    <option key={emp.value} value={emp.value}>
                      {emp.label}
                    </option>
                  ))}
                </Select>
                <Button
                  size="md"
                  borderRadius="md"
                  borderLeftRadius={0}
                  p={5}
                  colorScheme="whiteAlpha"
                  bg="#327171"
                  onClick={fetchTargets}
                >
                  Search
                </Button>
              </Flex>
            )}

            <Flex display="flex" alignItems="flex-end" w="260px">
              <Select
                placeholder="Select Status"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                color="#327171"
                borderRightRadius={0}
                focusBorderColor="teal"
                borderWidth="0.2px"
                _focus={{ borderWidth: "0.2px", borderColor: "teal.200" }}
              >
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
              </Select>
              <Button
                size="md"
                borderRadius="md"
                borderLeftRadius={0}
                p={5}
                colorScheme="whiteAlpha"
                bg="#327171"
                onClick={fetchTargets}
              >
                Search
              </Button>
            </Flex>
          </Flex>
        </Flex>

        {/* Targets Table */}
        <HrmsTableFrame>
          <table className="table">
            <thead>
              <tr>
                {isAdmin && <th>Employee</th>}
                <th>Title</th>
                <th>Description</th>
                <th>Deadline</th>
                <th>Progress</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredTargets.length > 0 ? (
                filteredTargets.map((target: any) => (
                  <tr key={target._id}>
                    {isAdmin && (
                      <td>
                        <PersonCell name={target.name || target.email} subtitle={target.email} />
                      </td>
                    )}
                    <td>{target.targetTitle || target.title || "-"}</td>
                    <td>{target.targetDescription || "-"}</td>
                    <td>
                      {target.deadline
                        ? DateTime.fromISO(target.deadline).toFormat("dd MMM yyyy")
                        : "-"}
                    </td>
                    <td>
                      <Flex align="center" gap={2}>
                        <Progress
                          value={target.progress || 0}
                          size="sm"
                          colorScheme="teal"
                          borderRadius="full"
                          w="80px"
                        />
                        <Text fontSize="xs" m={0}>{target.progress || 0}%</Text>
                      </Flex>
                    </td>
                    <td>{getStatusBadge(target.status)}</td>
                    <td>
                      <Button
                        size="xs"
                        bg="#327171"
                        color="white"
                        _hover={{ bg: "#285e5e" }}
                        onClick={() => {
                          setSelectedTarget(target);
                          setUpdateProgress(target.progress || 0);
                          setUpdateStatus(target.status || "pending");
                          onUpdateOpen();
                        }}
                      >
                        Update
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={isAdmin ? 7 : 6} style={{ textAlign: "center", color: "#a0aec0" }}>
                    No targets found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </HrmsTableFrame>

        {/* Create Target Modal (Admin) */}
        <Modal isOpen={isCreateOpen} onClose={onCreateClose} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Set New Target</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <FormControl mb={3} isRequired>
                <FormLabel>Employee</FormLabel>
                <Select
                  placeholder="Select Employee"
                  value={newTarget.email}
                  onChange={(e) => setNewTarget({ ...newTarget, email: e.target.value })}
                >
                  {employees.map((emp) => (
                    <option key={emp.value} value={emp.value}>
                      {emp.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl mb={3} isRequired>
                <FormLabel>Title</FormLabel>
                <Input
                  value={newTarget.targetTitle}
                  onChange={(e) => setNewTarget({ ...newTarget, targetTitle: e.target.value })}
                  placeholder="Target title"
                />
              </FormControl>
              <FormControl mb={3}>
                <FormLabel>Description</FormLabel>
                <Textarea
                  value={newTarget.targetDescription}
                  onChange={(e) => setNewTarget({ ...newTarget, targetDescription: e.target.value })}
                  placeholder="Target description"
                />
              </FormControl>
              <FormControl mb={3} isRequired>
                <FormLabel>Deadline</FormLabel>
                <Input
                  type="date"
                  value={newTarget.deadline}
                  min={bounds.target.min}
                  max={bounds.target.max}
                  onChange={(e) => setNewTarget({ ...newTarget, deadline: e.target.value })}
                />
              </FormControl>
            </ModalBody>
            <ModalFooter>
              <Button mr={3} onClick={onCreateClose}>Cancel</Button>
              <Button
                bg="#327171"
                color="white"
                onClick={handleCreateTarget}
                isLoading={isSubmitting}
                _hover={{ bg: "#285e5e" }}
              >
                Create
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Update Progress Modal */}
        <Modal isOpen={isUpdateOpen} onClose={onUpdateClose} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Update Target Progress</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {selectedTarget && (
                <>
                  <Text fontWeight="bold" mb={4}>{selectedTarget.title}</Text>
                  <FormControl mb={4}>
                    <FormLabel>Progress: {updateProgress}%</FormLabel>
                    <Slider
                      value={updateProgress}
                      onChange={setUpdateProgress}
                      min={0}
                      max={100}
                      step={5}
                    >
                      <SliderTrack>
                        <SliderFilledTrack bg="teal.500" />
                      </SliderTrack>
                      <SliderThumb />
                    </Slider>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Status</FormLabel>
                    <Select value={updateStatus} onChange={(e) => setUpdateStatus(e.target.value)}>
                      <option value="pending">Pending</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Completed</option>
                    </Select>
                  </FormControl>
                </>
              )}
            </ModalBody>
            <ModalFooter>
              <Button mr={3} onClick={onUpdateClose}>Cancel</Button>
              <Button bg="#327171" color="white" onClick={handleUpdateTarget} _hover={{ bg: "#285e5e" }}>
                Update
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </>
    </HrmsPageShell>
  );
};
